import { TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import { TimeBalanceEntry } from "@repo/server-api";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import * as Yup from "yup";

import { validErrorState } from "@repo/legacy/components/helper/Index";

export default function ConformationDialogWithContext(
	props: Readonly<{
		openPopup: boolean;
		setOpenDeleteConformation: (value: boolean) => void;
		handleYes: () => void;
		text: string;
		className?: string;
		info?: TimeBalanceEntry;
		setInfo?: (value: TimeBalanceEntry) => void;
		message?: string;
		setMessage?: (value: string) => void;
		handleClose?: () => void;
	}>,
) {
	const { t } = useTranslation();

	const [open, setOpen] = useState(false);
	const [message, setMessage] = useState<string>(props.message ?? "");
	const [info, setInfo] = useState<TimeBalanceEntry | null>(null);
	const [errors, setErrors] = useState<Record<string, boolean | object | string>>({});
	const [isLoaded, setIsLoaded] = useState<boolean>(false);

	useEffect(() => {
		setOpen(props.openPopup);
	}, [props.openPopup]);

	useEffect(() => {
		if (isLoaded && props.setInfo && info?.reason) {
			props.setInfo?.({
				...props.info,
				reason: info?.reason,
			} as TimeBalanceEntry);
		}
	}, [info?.reason]);

	useEffect(() => {
		if (props.setMessage) {
			props.setMessage(message);
		}
	}, [message]);

	useEffect(() => {
		if (props.info && !isLoaded) {
			setInfo({
				...props.info,
				reason: "",
			});
			setIsLoaded(true);
		}
	}, [props.info]);

	const handleClose = () => {
		setOpen(false);
		props.setOpenDeleteConformation(false);
		setErrors({});
		setIsLoaded(false);
		props.setInfo?.({
			...props.info,
			reason: "",
		} as TimeBalanceEntry);
		props.setMessage?.("");
		setMessage("");
		props.handleClose?.();
	};

	const validationServiceSchema = Yup.object().shape({
		reason: Yup.string().required(t("validation.reason.required")),
	});

	const validateFields = async () => {
		const localErrors: Record<string, boolean | object | string> = {};
		try {
			await validationServiceSchema.validate(info, { abortEarly: false });
		} catch (err) {
			if (err instanceof Yup.ValidationError) {
				err.inner.forEach((error) => {
					if (error.path) {
						localErrors[error.path] = error.message;
					}
				});
			}
		}

		setErrors({ ...localErrors });

		return validErrorState(localErrors);
	};

	return (
		<Dialog
			open={open}
			data-testid="confirmation-dialog"
			aria-describedby="alert-dialog-description">
			<DialogContent>
				<DialogContentText id="alert-dialog-description">{props.text}</DialogContentText>
				<TextField
					onChange={(event) => {
						setInfo({
							...info,
							reason: event.target.value,
						} as TimeBalanceEntry);
						setErrors({
							...errors,
							reason: "",
						});
						setMessage(event.target.value);
					}}
					className="w-100"
					sx={{ marginTop: 2 }}
					error={!!errors?.reason}
					helperText={errors?.reason as string}
					value={info?.reason ?? message ?? ""}
					label={t("reason")}
					variant="outlined"
					rows={4}
					multiline
				/>
			</DialogContent>
			<DialogActions>
				<Button
					data-testid="yes-button"
					onClick={async () => {
						const isValid = await validateFields();
						if (isValid) {
							props.handleYes();
							handleClose();
						}
					}}>
					{t("yes")}
				</Button>
				<Button data-testid="no-button" onClick={handleClose} autoFocus>
					{t("no")}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
